import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, Cloud, ExternalLink, Rocket, Send, Unlink } from "lucide-react";
import { api } from "@/lib/api";
import { Row, IndexNowStat } from "../_ui";

/* ---------- Bluesky (per-site social auto-post) ---------- */
export default function BlueskyPanel({ site }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'connect' | 'disconnect' | 'toggle' | 'test'
  const [flash, setFlash] = useState(null);
  const [handle, setHandle] = useState("");
  const [appPassword, setAppPassword] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/sites/${site.id}/bluesky/status`);
        if (!cancelled) { setStatus(r.data); setLoading(false); }
      } catch (e) {
        if (!cancelled) {
          setFlash({ kind: "err", msg: e?.response?.data?.detail || "Failed to load Bluesky status" });
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [site.id]);

  async function reload() {
    try {
      const r = await api.get(`/sites/${site.id}/bluesky/status`);
      setStatus(r.data);
    } catch (err) {
      setFlash({ kind: "err", msg: err?.response?.data?.detail || "Failed to refresh Bluesky status" });
    }
  }

  async function connect() {
    if (busy) return;
    if (!handle.trim() || !appPassword.trim()) {
      setFlash({ kind: "err", msg: "Handle and app password are required" });
      return;
    }
    setBusy("connect"); setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/bluesky/connect`, {
        handle: handle.trim(),
        app_password: appPassword.trim(),
      });
      setFlash({ kind: "ok", msg: `Connected as @${r.data.handle}` });
      setHandle(""); setAppPassword("");
      await reload();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Connection failed" });
    } finally { setBusy(null); }
  }

  async function disconnect() {
    if (busy) return;
    if (!window.confirm("Disconnect Bluesky? Stored credentials will be wiped and auto-posting will turn off.")) return;
    setBusy("disconnect"); setFlash(null);
    try {
      await api.post(`/sites/${site.id}/bluesky/disconnect`);
      setFlash({ kind: "ok", msg: "Disconnected" });
      await reload();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Disconnect failed" });
    } finally { setBusy(null); }
  }

  async function toggleAutoPost() {
    if (busy || !status?.connected) return;
    setBusy("toggle"); setFlash(null);
    try {
      const next = !status.auto_post_enabled;
      await api.post(`/sites/${site.id}/bluesky/auto-post`, { enabled: next });
      setFlash({ kind: "ok", msg: next ? "Auto-post enabled" : "Auto-post disabled" });
      await reload();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Toggle failed" });
    } finally { setBusy(null); }
  }

  async function testPost() {
    if (busy) return;
    setBusy("test"); setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/bluesky/test-post`, {});
      if (r.data.ok) {
        setFlash({ kind: "ok", msg: "Test post sent — check your Bluesky feed" });
      } else {
        setFlash({ kind: "err", msg: `Test failed: ${r.data.result?.reason || "unknown"}` });
      }
      await reload();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Test post failed" });
    } finally { setBusy(null); }
  }

  if (loading) return <div className="text-xs text-zinc-500 mono pl-[200px]">Loading Bluesky status…</div>;
  if (!status) return <div className="text-xs text-rose-400 mono pl-[200px]">Failed to load Bluesky status</div>;

  const stats = status.stats || {};
  const profileUrl = status.handle ? `https://bsky.app/profile/${status.handle}` : null;

  return (
    <div className="space-y-3" data-testid="bluesky-panel">
      <Row label="Status">
        <div className="flex items-center gap-2 flex-wrap">
          {status.connected ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm bg-sky-900/40 border border-sky-700 text-sky-300 mono uppercase tracking-wider" data-testid="bluesky-badge-connected">
              <CheckCircle2 size={11}/> Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-400 mono uppercase tracking-wider" data-testid="bluesky-badge-disconnected">
              <Circle size={11}/> Not connected
            </span>
          )}
          {status.connected && status.auto_post_enabled && (
            <span className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-sm bg-emerald-900/40 border border-emerald-700 text-emerald-300 mono uppercase tracking-wider" data-testid="bluesky-badge-autopost-on">
              <Rocket size={11}/> Auto-post ON
            </span>
          )}
          {status.handle && (
            <a href={profileUrl} target="_blank" rel="noreferrer" className="text-[11px] text-sky-400 hover:text-sky-300 underline mono" data-testid="bluesky-profile-link">
              @{status.handle}
            </a>
          )}
        </div>
      </Row>

      {!status.connected && (
        <>
          <Row label="Bluesky handle">
            <input
              data-testid="bluesky-handle-input"
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="finances4smart.bsky.social"
              className="bg-zinc-950 border border-zinc-800 focus:border-sky-600 rounded-sm px-3 py-1.5 text-sm w-full max-w-md mono"
            />
          </Row>
          <Row label="App password">
            <div className="space-y-1">
              <input
                data-testid="bluesky-apppw-input"
                type="password"
                value={appPassword}
                onChange={(e) => setAppPassword(e.target.value)}
                placeholder="xxxx-xxxx-xxxx-xxxx"
                className="bg-zinc-950 border border-zinc-800 focus:border-sky-600 rounded-sm px-3 py-1.5 text-sm w-full max-w-md mono"
              />
              <a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noreferrer" className="text-[11px] text-sky-400 hover:text-sky-300 underline mono inline-flex items-center gap-1">
                Generate at bsky.app/settings/app-passwords <ExternalLink size={9}/>
              </a>
            </div>
          </Row>
          <Row label="">
            <button
              onClick={connect}
              disabled={busy}
              data-testid="bluesky-connect-btn"
              className="inline-flex items-center gap-1.5 bg-sky-700 hover:bg-sky-600 disabled:opacity-40 text-white px-3 py-1.5 rounded-sm text-xs font-semibold mono"
            >
              <Cloud size={12}/> {busy === "connect" ? "Connecting…" : "Connect Bluesky"}
            </button>
          </Row>
        </>
      )}

      {status.connected && (
        <>
          <Row label="Stats">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <IndexNowStat label="Published" value={stats.published_articles ?? 0} testid="bluesky-stat-published"/>
              <IndexNowStat label="Posted" value={stats.published_articles_posted ?? 0} testid="bluesky-stat-posted" tone={stats.published_articles_posted === stats.published_articles ? "ok" : "warn"}/>
              <IndexNowStat label="Total posts" value={stats.total_posts ?? 0} testid="bluesky-stat-total"/>
              <IndexNowStat label="Last post" value={stats.last_post_at ? new Date(stats.last_post_at).toLocaleString() : "—"} testid="bluesky-stat-last"/>
            </div>
          </Row>

          {stats.last_status && (
            <Row label="Last result">
              <div className="font-mono text-[11px] text-zinc-400" data-testid="bluesky-last-status">
                <span className={stats.last_status.ok ? "text-emerald-400" : "text-rose-400"}>
                  {stats.last_status.ok ? "OK" : "FAIL"}
                </span>
                {stats.last_status.reason ? ` · ${stats.last_status.reason}` : ""}
                {stats.last_status.event ? ` · ${stats.last_status.event}` : ""}
                {stats.last_status.uri ? (
                  <a
                    href={`https://bsky.app/profile/${status.handle}/post/${stats.last_status.uri.split("/").pop()}`}
                    target="_blank" rel="noreferrer"
                    className="ml-2 text-sky-400 hover:text-sky-300 underline"
                  >view post →</a>
                ) : ""}
              </div>
            </Row>
          )}

          <Row label="Actions">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={toggleAutoPost}
                disabled={busy}
                data-testid="bluesky-toggle-btn"
                className={`inline-flex items-center gap-1.5 ${status.auto_post_enabled ? "bg-emerald-700 hover:bg-emerald-600" : "border border-zinc-700 hover:border-emerald-500 text-zinc-300"} text-white px-3 py-1.5 rounded-sm text-xs font-semibold mono disabled:opacity-40`}
              >
                <Rocket size={12}/> {busy === "toggle" ? "Saving…" : (status.auto_post_enabled ? "Disable auto-post" : "Enable auto-post")}
              </button>
              <button
                onClick={testPost}
                disabled={busy}
                data-testid="bluesky-test-btn"
                className="inline-flex items-center gap-1.5 border border-sky-700 hover:bg-sky-900/40 text-sky-300 px-3 py-1.5 rounded-sm text-xs mono disabled:opacity-40"
              >
                <Send size={12}/> {busy === "test" ? "Posting…" : "Send test post"}
              </button>
              <button
                onClick={disconnect}
                disabled={busy}
                data-testid="bluesky-disconnect-btn"
                className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-rose-500 hover:text-rose-300 text-zinc-300 px-3 py-1.5 rounded-sm text-xs mono disabled:opacity-40"
              >
                <Unlink size={12}/> {busy === "disconnect" ? "…" : "Disconnect"}
              </button>
            </div>
          </Row>

          <BlueskyRoundupSection site={site} status={status} onChanged={reload} />
        </>
      )}

      {flash && (
        <div className={`text-[11px] mono pl-[200px] ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`} data-testid="bluesky-flash">
          {flash.msg}
        </div>
      )}

      <div className="text-[11px] text-zinc-500 mono pl-[200px]">
        Bluesky is free, open (AT Protocol), and has no app review. Every publish + bulk-publish + auto-publish posts a link card with article thumbnail. Toggle off any time.
      </div>
    </div>
  );
}

/* ---------- Bluesky Daily Roundup (1 threaded post per day, top N articles) ---------- */
function BlueskyRoundupSection({ site, status, onChanged }) {
  const r = status.roundup || {};
  const enabled = !!r.enabled;
  const hour = r.hour_utc ?? 14;
  const topN = r.top_n ?? 3;
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null);

  async function save(next) {
    if (busy) return;
    setBusy("save"); setFlash(null);
    try {
      await api.post(`/sites/${site.id}/bluesky/roundup/config`, {
        enabled: next.enabled ?? enabled,
        hour_utc: next.hour ?? hour,
        top_n: next.topN ?? topN,
      });
      setFlash({ kind: "ok", msg: next.enabled === false ? "Roundup disabled" : "Saved" });
      if (onChanged) await onChanged();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Save failed" });
    } finally { setBusy(null); }
  }

  async function triggerNow() {
    if (busy) return;
    if (!window.confirm("Post today's roundup right now? This bypasses the once-per-day gate and posts immediately.")) return;
    setBusy("trigger"); setFlash(null);
    try {
      const resp = await api.post(`/sites/${site.id}/bluesky/roundup/trigger`);
      if (resp.data.ok) {
        const posted = resp.data.result?.posted_count ?? 0;
        setFlash({ kind: "ok", msg: `Roundup posted: 1 header + ${posted} article ${posted === 1 ? "reply" : "replies"}` });
      } else {
        setFlash({ kind: "err", msg: `Failed: ${resp.data.reason || "unknown"}` });
      }
      if (onChanged) await onChanged();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Trigger failed" });
    } finally { setBusy(null); }
  }

  const last = r.last_status || {};
  const headerUri = last.header_uri;
  const headerPostId = headerUri ? headerUri.split("/").pop() : null;
  const handle = status.handle;

  return (
    <>
      <div className="pt-4 mt-2 border-t border-zinc-900 label-tiny">Daily roundup (threaded top-{topN} post)</div>

      <Row label="Enable">
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => save({ enabled: e.target.checked })}
              disabled={busy}
              data-testid="bluesky-roundup-enable"
              className="accent-emerald-600"
            />
            <span className="text-sm">{enabled ? "ON — one threaded post per day" : "OFF"}</span>
          </label>
        </div>
      </Row>

      {enabled && (
        <>
          <Row label="Post at (UTC hour)">
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={hour}
                onChange={(e) => save({ hour: parseInt(e.target.value, 10) })}
                disabled={busy}
                data-testid="bluesky-roundup-hour"
                className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1 text-sm mono"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={`hour-${i}`} value={i}>{String(i).padStart(2, "0")}:00 UTC</option>
                ))}
              </select>
              <span className="text-[11px] text-zinc-500 mono">
                = {String((hour - 5 + 24) % 24).padStart(2, "0")}:00 ET · {String((hour - 8 + 24) % 24).padStart(2, "0")}:00 PT
              </span>
            </div>
          </Row>
          <Row label="Articles in thread">
            <div className="flex items-center gap-2">
              {[2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => save({ topN: n })}
                  disabled={busy}
                  data-testid={`bluesky-roundup-topn-${n}`}
                  className={`mono text-xs px-2.5 py-1 rounded-sm border ${topN === n ? "bg-sky-700 border-sky-600 text-white" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"} disabled:opacity-40`}
                >
                  {n}
                </button>
              ))}
              <span className="text-[11px] text-zinc-500 mono ml-1">top {topN} by quality score</span>
            </div>
          </Row>
        </>
      )}

      <Row label="Stats">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <IndexNowStat label="Roundups sent" value={r.total ?? 0} testid="bluesky-roundup-stat-total"/>
          <IndexNowStat label="Last sent" value={r.last_at ? new Date(r.last_at).toLocaleString() : "—"} testid="bluesky-roundup-stat-last"/>
          <IndexNowStat label="Last result" value={last.ok ? `OK · ${last.posted_count ?? 0} articles` : (last.reason || "—")} tone={last.ok ? "ok" : (r.last_at ? "warn" : undefined)} testid="bluesky-roundup-stat-result"/>
        </div>
      </Row>

      <Row label="Actions">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={triggerNow}
            disabled={busy}
            data-testid="bluesky-roundup-trigger-btn"
            className="inline-flex items-center gap-1.5 border border-sky-700 hover:bg-sky-900/40 text-sky-300 px-3 py-1.5 rounded-sm text-xs mono disabled:opacity-40"
          >
            <Send size={12}/> {busy === "trigger" ? "Posting…" : "Post roundup now"}
          </button>
          {headerPostId && handle && (
            <a
              href={`https://bsky.app/profile/${handle}/post/${headerPostId}`}
              target="_blank" rel="noreferrer"
              data-testid="bluesky-roundup-view-link"
              className="inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300 underline mono"
            >
              View last thread <ExternalLink size={9}/>
            </a>
          )}
        </div>
      </Row>

      {flash && (
        <div className={`text-[11px] mono pl-[200px] ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`} data-testid="bluesky-roundup-flash">
          {flash.msg}
        </div>
      )}

      <div className="text-[11px] text-zinc-500 mono pl-[200px]">
        A single thread out-performs N separate posts in early-stage feeds — each reply lifts the header into the timeline again, and the thread context drives more clicks. Articles also chosen by quality score.
      </div>
    </>
  );
}
